import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { generateClient } from '@aws-amplify/api';
import * as d3 from 'd3';
import { getRecruitmentSources, getSites, getStudies, getSubjectDealStatusCounts, getSubjectEnrollmentStatusCounts } from '@/graphql/queries';
import SelectorTwoLine from '../SelectorTwoLine';
import Funnel from '@/Funnel_v2';
import ErrorModal from '@/ErrorModal';

const client = generateClient();

const level1Options = [
	{ id: 'all', name: 'All Data', description: 'Complete unfiltered breakout' },
	{ id: 'site', name: 'Site', description: 'Site specific breakout' },
	{ id: 'study', name: 'Study', description: 'Study specific breakout' },
	{ id: 'source', name: 'Recruitment Source', description: 'Recruitment Source specific breakout' }
];

export default ({ path = '/app/pipeline/subjects/' }) => {
	const isConstructed = useRef(false);
	const location = useLocation();
	const navigate = useNavigate();
	const [error, setError] = useState();
	const [busy, setBusy] = useState(true);
	const [recruitmentSources, setRecruitmentSources] = useState([]);
	const [level1, setLevel1] = useState(level1Options.find(e => e.id === 'study'));
	const [sites, setSites] = useState([]);
	const [studies, setStudies] = useState([]);
	const [subjectEnrollmentStatusCounts, setSubjectEnrollmentStatusCounts] = useState([]);
	const [subjectDealStatusCounts, setSubjectDealStatusCounts] = useState([]);
	const [variables, setVariables] = useState();
	const [graphHeader, setGraphHeader] = useState();

	const lastVariables = useRef(variables);
	useEffect(() => { lastVariables.current = variables; }, [JSON.stringify(variables)]);

	const catchError = error => {
		if (error instanceof Error) {
			setError(error.toString());
		} else {
			setError((error.errors || []).map(e => e.message?.toString()));
		}
	};

	const regen = [JSON.stringify(variables), JSON.stringify({ level1, sites, studies, recruitmentSources })];

	const load = useCallback(({ level1, variables }) => {
		const promises = [];
		if (!isConstructed.current) {
			promises.push(client.graphql({ query: getSites, variables: { sorting: { field: 'site_name', order: 'asc' } } })
				.then(response => {
					setSites([{ site_name: 'All Sites' }, ...response.data.getSites.sites]);
				})
			);
		}
		if (!isConstructed.current) {
			promises.push(client.graphql({ query: getStudies, variables: { sorting: { field: 'study_name', order: 'asc' } } })
				.then(response => {
					setStudies([{ study_name: 'All Studies' }, ...response.data.getStudies.studies]);
				})
			);
		}
		if (!isConstructed.current) {
			promises.push(client.graphql({ query: getRecruitmentSources, variables: { sorting: { field: 'source_name', order: 'asc' } } })
				.then(response => {
					setRecruitmentSources([{ source_name: 'All Recruitment Sources' }, ...response.data.getRecruitmentSources.recruitment_sources]);
				})
			);
		}
		if (!isConstructed.current ||
			variables?.site_id !== lastVariables.current?.site_id ||
			variables?.study_id !== lastVariables.current?.study_id ||
			variables?.recruitment_id !== lastVariables.current?.recruitment_id
		) {
			promises.push(client.graphql({ query: getSubjectEnrollmentStatusCounts, variables })
				.then(response => {
					setSubjectEnrollmentStatusCounts({
						data: response.data.getSubjectEnrollmentStatusCounts.subject_enrollment_status_counts,
						data_raw: response.data.getSubjectEnrollmentStatusCounts.subject_enrollment_status_counts_raw
					});
				})
			);
			promises.push(client.graphql({ query: getSubjectDealStatusCounts, variables })
				.then(response => {
					setSubjectDealStatusCounts({
						data: response.data.getSubjectDealStatusCounts.subject_deal_status_counts,
						data_raw: response.data.getSubjectDealStatusCounts.subject_deal_status_counts_raw
					});
				})
			);
		}
		if (promises.length > 0) {
			setBusy(true);
			setLevel1(level1);
			Promise.all(promises)
				.then(() => {
					setLevel1(level1);
					setBusy(false);
				})
				.catch(catchError);
		}
	}, [JSON.stringify(lastVariables.current), JSON.stringify(level1), regen]);

	const loadNames = useCallback(variables => {
		let entry;
		if (variables.recruitment_id && (entry = recruitmentSources.find(e => e.id === variables.recruitment_id))) {
			variables.recruitment_name = entry.source_name;
		} else {
			!variables.recruitment_id && delete variables.recruitment_name;
		}
		if (variables.site_id && (entry = sites.find(e => e.id === variables.site_id))) {
			variables.site_name = entry.site_name;
		} else {
			!variables.site_id && delete variables.site_name;
		}
		if (variables.study_id && (entry = studies.find(e => e.id === variables.study_id))) {
			variables.study_name = entry.study_name;
		} else {
			!variables.study_id && delete variables.study_name;
		}
	}, regen);

	if (!isConstructed.current) {
		const queryParams = new URLSearchParams(location.search);
		const recruitment_id = queryParams.get('recruitment_id') || undefined;
		const site_id = queryParams.get('site_id') || undefined;
		const study_id = queryParams.get('study_id') || undefined;
		let id = level1.id;
		if (recruitment_id) id = 'source';
		else if (site_id) id = 'site';
		else if (study_id) id = 'study';
		const newLevel1 = level1Options.find(e => e.id === id);
		const newVariables = {
			recruitment_id: isNaN(recruitment_id) ? undefined : parseInt(recruitment_id, 10),
			recruitment_name: queryParams.get('recruitment_name'),
			site_id: isNaN(site_id) ? undefined : parseInt(site_id, 10),
			site_name: queryParams.get('site_name'),
			study_id: isNaN(study_id) ? undefined : parseInt(study_id, 10),
			study_name: queryParams.get('study_name')
		};
		loadNames(newVariables);
		setVariables(newVariables);
		load({ level1: newLevel1, variables: newVariables });
		isConstructed.current = true;
	}	

	// Updates URL query with the current variables
	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		// TESTING
		//queryParams.delete('enrollment_status');
		['recruitment_id', 'recruitment_name', 'site_id', 'site_name', 'study_id', 'study_name'].forEach(filterName => {
			if (!variables?.[filterName] || variables[filterName].length === 0) {
				queryParams.delete(filterName);
			} else {
				queryParams.set(filterName, variables[filterName]);
			}
		});
		navigate({ search: `?${queryParams.toString()}` }, { replace: true });
	}, [JSON.stringify(variables)]);

	useEffect(() => {
		if (location.search?.length === 0) {
			// Special case where we want the empty location.search to drive us back to unfiltered
			const newVariables = validateVariables({ level1, variables: {} });
			setVariables(newVariables);
			load({ level1, variables: newVariables });
		}
	}, [location.search]);

	useEffect(() => {
		// Side effect for updating the name & description
		validateVariables({ level1, variables });
	}, [JSON.stringify({ recruitmentSources, sites, studies })]);

	const validateVariables = useCallback(({ level1, variables }) => {
		const site_id = sites.find(e => e.id === variables?.site_id) ? variables?.site_id : undefined;
		const study_id = studies.find(e => e.id === variables?.study_id) ? variables?.study_id : undefined;
		const recruitment_id = recruitmentSources.find(e => e.id === variables?.recruitment_id) ? variables?.recruitment_id : undefined;
		let name;
		let description;
		let total_spend;
		if (level1.id === 'site') {
			const d = sites.find(e => e.id === site_id) || sites[0];
			name = d?.site_name;
			description = d?.site_description;
		} else if (level1.id === 'study') {
			const d = studies.find(e => e.id === study_id) || studies[0];
			name = d?.study_name;
			description = d?.study_description;
		} else if (level1.id === 'source') {
			const d = recruitmentSources.find(e => e.id === recruitment_id) || recruitmentSources[0];
			name = d?.source_name;
			description = d?.source_description;
			total_spend = d?.total_spend;
			// Add in the children total_spend
			total_spend += _.sum(recruitmentSources.filter(e => e.parent_id === recruitment_id).map(e => e.total_spend));
		}
		setGraphHeader({
			description,
			name,
			total_spend
		});
		return { site_id, study_id, recruitment_id };
	}, [regen]);

	const onChangeLevel1Handler = useCallback(option => {
		setLevel1(option);
		const newVariables = validateVariables({ level1: option, variables: {} });
		setVariables(newVariables);
		load({ level1: option, variables: newVariables });
	}, regen);

	const onChangeSiteHandler = useCallback(option => {
		const site_id = parseInt(option.id, 10);
		const _level1 = level1Options.find(e => e.id === 'site');
		// Note: Forcing exclusivity since we don't know how to breadcrumb multiple filters
		const newVariables = validateVariables({ level1: _level1, variables: { site_id } });
		loadNames(newVariables);
		setVariables(newVariables);
		load({ level1: _level1, variables: newVariables });
	}, regen);

	const onChangeStudyHandler = useCallback(option => {
		const study_id = parseInt(option.id, 10);
		const _level1 = level1Options.find(e => e.id === 'study');
		// Note: Forcing exclusivity since we don't know how to breadcrumb multiple filters
		const newVariables = validateVariables({ level1: _level1, variables: { study_id } });
		loadNames(newVariables);
		setVariables(newVariables);
		load({ level1: _level1, variables: newVariables });
	}, regen);

	const onChangeSourceHandler = useCallback(option => {
		const recruitment_id = parseInt(option.id, 10);
		const _level1 = level1Options.find(e => e.id === 'source');
		// Note: Forcing exclusivity since we don't know how to breadcrumb multiple filters
		const newVariables = validateVariables({ level1: _level1, variables: { recruitment_id } });
		loadNames(newVariables);
		setVariables(newVariables);
		load({ level1: _level1, variables: newVariables });
	}, regen);

	const pill = text => (<span className="col-auto ms-3 me-2 badge text-bg-primary rounded-pill" style={{padding: '6px 8px'}}>{text}</span>);

	// figure out what's currently selected (parent or child)
	const selectedSource = recruitmentSources.find(
		r => String(r.id) === String(variables?.recruitment_id)
	);

	// if a child is selected, parentSourceId = its parent_id; otherwise it's the selected id
	const parentSourceId = selectedSource?.parent_id
		? selectedSource.parent_id
		: selectedSource?.id;

	// campaigns = children of the selected top-level source
	const campaignOptions = parentSourceId
		? recruitmentSources.filter(r => r.parent_id === parentSourceId)
		: [];

	// if a child campaign is currently selected, keep it as the select's value; else empty
	const selectedCampaignId = selectedSource?.parent_id ? variables?.recruitment_id : "";

	const enrollment_status_count = _.sumBy(subjectEnrollmentStatusCounts.data_raw, 'count');
	const deal_status_count = _.sumBy(subjectDealStatusCounts.data_raw, 'count');

	return (
		<div className="mt-3">
			{error && <ErrorModal message={error} onClose={() => setError()} title="Dashboard Error" />}
			<div className="d-flex align-items-center justify-content-between mb-2">
				<h2 className="m-0 header-title">AVA’s Recruitment Pipeline</h2>
			</div>

			{/* Helper line */}
			<div className="text-muted helper mb-3 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
				<p className="mb-0">Select your desired level to view detailed metrics and insights</p>
			</div>

	      	{/* Filters row */}
      		<div className="row g-4">
				{/* Site */}
				<div className="col-12 col-md-6 col-lg-3">
					<select
						className="form-select chip-select"
						value={variables?.site_id ?? ""}               // show current site from URL/state
						onChange={(e) => {
							const id = e.target.value;                   // "" means All Sites
							const option = id ? sites.find(s => String(s.id) === id) : { id: undefined };
							onChangeSiteHandler(option || { id: undefined });
						}}
					>
						<option value="">All Sites</option>
						{sites
							.filter(s => s.id)                           // skip the synthetic "All Sites" object without id
							.map(s => (
								<option key={s.id} value={s.id}>{s.site_name}</option>
							))}
					</select>
				</div>
				{/* Study */}
				<div className="col-12 col-md-6 col-lg-3">
					<select
						className="form-select chip-select"
						value={variables?.study_id ?? ""}
						onChange={(e) => {
							const id = e.target.value;
							const option = id ? studies.find(s => String(s.id) === id) : { id: undefined };
							onChangeStudyHandler(option || { id: undefined });
						}}
					>
						<option value="">All Studies</option>
						{studies
							.filter(s => s.id)
							.map(s => (
								<option key={s.id} value={s.id}>{s.study_name}</option>
							))}
					</select>
				</div>
				{/* Recruitment Sources */}
				<div className="col-12 col-md-6 col-lg-3">
					<select
						className="form-select chip-select"
						value={variables?.recruitment_id ?? ""}
						onChange={(e) => {
							const id = e.target.value;
							const option = id
								? recruitmentSources.find(r => String(r.id) === id)
								: { id: undefined };
							onChangeSourceHandler(option || { id: undefined });
						}}
					>
						<option value="">All Recruitment Sources</option>
						{recruitmentSources
							.filter(r => r.id && !r.parent_id)           // top-level sources only, like before
							.map(r => (
								<option key={r.id} value={r.id}>{r.source_name}</option>
							))}
					</select>
				</div>
				{/* Facebook Campaign */}
				<div className={`col-12 col-md-6 col-lg-3 ${level1.id === 'source' && variables?.recruitment_id ? '' : 'invisible'}`}>
					<select
						className="form-select chip-select"
						value={selectedCampaignId}
						onChange={(e) => {
							const id = e.target.value;

							// empty -> go back to parent (no specific campaign)
							if (!id) {
								const parentOption =
									recruitmentSources.find(r => String(r.id) === String(parentSourceId)) || { id: undefined };
								onChangeSourceHandler(parentOption);
								return;
							}

							// pick a real child campaign
							const option = recruitmentSources.find(r => String(r.id) === id);
							onChangeSourceHandler(option || { id: undefined });
						}}
						disabled={!parentSourceId || campaignOptions.length === 0}
					>
						<option value="">
							{campaignOptions.length ? "Select a campaign…" : "No campaigns"}
						</option>
						{campaignOptions.map(c => (
							<option key={c.id} value={c.id}>{c.source_name}</option>
						))}
					</select>
				</div>

      </div>
			<hr className="my-4" />
			{graphHeader ? (
				<React.Fragment key={graphHeader.name}>
					<h3 className="mb-0">{graphHeader.name}<span style={{fontSize: '20px'}}>{graphHeader.total_spend ? ` (${d3.format('$,.2f')(graphHeader.total_spend)})` : undefined}</span></h3>
					<p>{graphHeader.description}</p>
				</React.Fragment>
			): undefined}
			{busy ? undefined : (
				<>
					{enrollment_status_count > 0 &&
						<div className="row position-relative" style={{zIndex: 1}}>
							<Funnel
								data={subjectEnrollmentStatusCounts.data.map(e => ({ label: e.status, priority: e.priority, value: e.count }))}
								data_raw={subjectEnrollmentStatusCounts.data_raw.map(e => ({ label: e.status, priority: e.priority, value: e.count }))}
								graph_header={graphHeader}
								path={path}
								status_key="enrollment_status"
								{...variables}
							/>
						</div>
					}
					{enrollment_status_count > 0 && deal_status_count > 0 && <hr />}
					{deal_status_count > 0 &&
						<div className="row position-relative" style={{zIndex: 1}}>
							<Funnel
								data={subjectDealStatusCounts.data.map(e => ({ label: e.status, priority: e.priority, value: e.count }))}
								data_raw={subjectDealStatusCounts.data_raw.map(e => ({ label: e.status, priority: e.priority, value: e.count }))}
								graph_header={graphHeader}
								path={path}
								status_key="deal_status"
								{...variables}
							/>
						</div>
					}
				</>
			)}
		</div>
	);
};
